/**
 * <s-phone-checkout> runs the PrintInc phone checkout inside the theme
 * (templates/cart.phone-checkout.liquid): mobile number -> WhatsApp code (skipped when already logged in) -> address -> Razorpay / COD payment (no Shopify checkout).
 *
 * It speaks the same contract as the PrintInc Phone Checkout app's own page, POSTing JSON to the
 * app proxy (data-endpoint, /apps/phone-checkout):
 *   start                         -> flow fields, sent back with every later call
 *   send     { phone }            -> WhatsApp code sent
 *   verify   { phone, code }      -> { proof }
 *   checkout { address…, phone, proof } -> { razorpay | cod | paymentResult } shown by printinc-payment.js
 * Errors come back as { error } with a non-2xx status and are shown as-is.
 * If `start` fails, [data-fallback] reveals the app's own page.
 *
 * Messages come from data-msg-* attributes (locales: phone_checkout.*).
 */
class SPhoneCheckout extends HTMLElement {
  flow = null;
  phone = '';
  proof = '';
  busy = false;
  resendAt = 0;

  connectedCallback() {
    this.forms = {
      phone: this.querySelector('[data-form="phone"]'),
      code: this.querySelector('[data-form="code"]'),
      address: this.querySelector('[data-form="address"]'),
    };
    if (!this.forms.phone) return; // empty cart view

    this.forms.phone.addEventListener('submit', (event) => this.onPhone(event));
    this.forms.code.addEventListener('submit', (event) => this.onCode(event));
    this.forms.address.addEventListener('submit', (event) => this.onAddress(event));
    this.querySelector('[data-resend]').addEventListener('click', () => this.onResend());
    this.querySelectorAll('[data-change]').forEach((button) => button.addEventListener('click', () => this.restart()));

    // Keep the phone field to digits only, and the code field to six digits.
    const phoneInput = this.forms.phone.elements.phone;
    phoneInput.addEventListener('input', () => {
      phoneInput.value = phoneInput.value.replace(/\D/g, '').replace(/^(91|0)(?=\d{10})/, '').slice(0, 10);
    });
    const codeInput = this.forms.code.elements.code;
    codeInput.addEventListener('input', () => {
      codeInput.value = codeInput.value.replace(/\D/g, '').slice(0, 6);
      if (codeInput.value.length === 6) this.forms.code.requestSubmit();
    });

    this.timer = setInterval(() => this.updateResend(), 1000);
    this.restart().then(() => this.useSavedLogin());
  }

  // Shoppers already logged in with WhatsApp (header / Buy it now popup) skip straight to the address step.
  async useSavedLogin() {
    if (!window.PrintIncAuth?.resume || this.proof) return;
    try {
      const session = await window.PrintIncAuth.resume();
      if (!session?.loggedIn || !session.phone || !session.proof) return;
      if (session.nonce && session.token) this.flow = { nonce: session.nonce, token: session.token };
      this.phone = session.phone;
      this.proof = session.proof;
      this.querySelector('[data-verified]').textContent = this.msg('Verified').replace('{{ phone }}', this.phone);
      this.step(2);
      this.status('');
    } catch {
      // Network trouble: keep the normal WhatsApp code step.
    }
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  msg(name) {
    return this.dataset[`msg${name}`] || '';
  }

  status(message, error = false) {
    const node = this.querySelector('[data-status]');
    node.textContent = message;
    node.toggleAttribute('data-error', error);
  }

  async api(action, data = {}) {
    const response = await fetch(this.dataset.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ action, ...this.flow, ...data }),
      signal: AbortSignal.timeout(20000),
    });
    let result = {};
    try {
      result = await response.json();
    } catch {
      // Non-JSON answer (proxy error page): handled below.
    }
    if (!response.ok) throw new Error(result.error || this.msg('Error'));
    return result;
  }

  async run(task) {
    if (this.busy) return;
    this.busy = true;
    this.setButtons(true);
    this.status(this.msg('Wait'));
    try {
      await task();
    } catch (error) {
      this.status(error.message || this.msg('Error'), true);
    } finally {
      this.busy = false;
      this.setButtons(false);
    }
  }

  setButtons(disabled) {
    this.querySelectorAll('[data-panel] button').forEach((button) => (button.disabled = disabled));
    if (!disabled) {
      this.querySelector('[data-send]').disabled = !this.flow;
      this.updateResend();
    }
  }

  updateResend() {
    const button = this.querySelector('[data-resend]');
    if (!button) return;
    const seconds = Math.max(0, Math.ceil((this.resendAt - Date.now()) / 1000));
    button.disabled = this.busy || seconds > 0;
    button.textContent = seconds ? this.msg('ResendIn').replace('{{ seconds }}', seconds) : this.msg('Resend');
  }

  step(number) {
    this.querySelectorAll('.phone-checkout__step').forEach((item) => {
      const index = Number(item.dataset.step);
      item.toggleAttribute('aria-current', index === number);
      item.toggleAttribute('data-done', index < number);
    });
    this.querySelector('[data-panel="phone"]').hidden = number !== 1;
    this.querySelector('[data-panel="address"]').hidden = number !== 2;
  }

  fullPhone() {
    return `+91${this.forms.phone.elements.phone.value}`;
  }

  restart() {
    this.proof = '';
    this.phone = '';
    this.flow = null;
    this.forms.phone.hidden = false;
    this.forms.code.hidden = true;
    this.forms.code.reset();
    this.step(1);
    this.status('');
    return this.run(async () => {
      try {
        this.flow = await this.api('start');
        this.querySelector('[data-fallback]').hidden = true;
        this.status('');
      } catch (error) {
        this.querySelector('[data-fallback]').hidden = false;
        throw new Error(error.message && error.message !== this.msg('Error') ? `${error.message} ${this.msg('Unavailable')}` : this.msg('Unavailable'));
      }
    });
  }

  onPhone(event) {
    event.preventDefault();
    if (!this.forms.phone.reportValidity()) return;
    this.run(async () => {
      this.phone = this.fullPhone();
      await this.api('send', { phone: this.phone });
      this.resendAt = Date.now() + 60000;
      this.forms.phone.hidden = true;
      this.forms.code.hidden = false;
      this.querySelector('[data-sent-to]').textContent = this.msg('Sent').replace('{{ phone }}', this.phone);
      this.forms.code.elements.code.focus();
      this.status('');
    });
  }

  onResend() {
    this.run(async () => {
      await this.api('send', { phone: this.phone });
      this.resendAt = Date.now() + 60000;
      this.status(this.msg('Resent'));
    });
  }

  onCode(event) {
    event.preventDefault();
    if (!this.forms.code.reportValidity()) return;
    this.run(async () => {
      const result = await this.api('verify', { phone: this.phone, code: this.forms.code.elements.code.value });
      this.proof = result.proof;
      window.PrintIncAuth?.remember?.(result); // stay logged in across the site until Log out
      this.querySelector('[data-verified]').textContent = this.msg('Verified').replace('{{ phone }}', this.phone);
      this.step(2);
      this.forms.address.elements.first_name.focus();
      this.status('');
    });
  }

  onAddress(event) {
    event.preventDefault();
    if (!this.forms.address.reportValidity()) return;
    this.run(async () => {
      const data = Object.fromEntries(new FormData(this.forms.address));
      data.discount = String(data.discount || '').trim().toUpperCase();
      const cartResponse=await fetch('/cart.js',{cache:'no-store'});
      if(!cartResponse.ok)throw new Error('Unable to load your cart.');
      const cart=await cartResponse.json();
      if(!cart.item_count)throw new Error('Your cart is empty.');
      const result = await this.api('checkout', { ...data, phone: this.phone, proof: this.proof,cart:{currency:cart.currency,items:cart.items.map(i=>({variant_id:i.variant_id,quantity:i.quantity})),total:cart.total_price} });
      if (result.razorpay || result.cod || result.paymentResult) {
        if (!window.PrintIncPayment) throw new Error('Payment screen is loading. Please try again.');
        this.forms.address.hidden = true;
        await window.PrintIncPayment({result,container:this,phone:this.phone,email:data.email,status:message=>this.status(message),confirm:response=>this.api('payment-confirm',{...response,phone:this.phone,proof:this.proof}),codConfirm:()=>this.api('cod-confirm',{phone:this.phone,proof:this.proof})});
        return;
      }
      // Shopify's native checkout is switched off for PrintInc: never hand off to /checkout.
      throw new Error(this.msg('Unavailable') || this.msg('Error'));
    });
  }
}

if (!customElements.get('s-phone-checkout')) customElements.define('s-phone-checkout', SPhoneCheckout);
